import { type MeDto, type PublicRoomState } from '@spin/shared';
import { useEffect, useRef, useState } from 'react';
import { http } from '../api/http';
import { Avatar } from '../components/common';
import { MusicVideoIcon, SongIcon, SpeakerIcon } from '../components/icons';
import { reportError, useT } from '../i18n';
import { serverNow } from '../store/room';
import { useSession } from '../store/session';
import { useUi } from '../store/ui';

/** What the YouTube player is doing (also exposed as data-player-state for styling and tests). */
type PlayerState = 'idle' | 'loading' | 'playing' | 'paused' | 'error';

// YT.PlayerState: -1 unstarted, 0 ended, 1 playing, 2 paused, 3 buffering, 5 cued.
const YT_STATES: Record<number, PlayerState> = {
  [-1]: 'loading',
  0: 'idle',
  1: 'playing',
  2: 'paused',
  3: 'loading',
  5: 'loading',
};

interface YtPlayer {
  loadVideoById(o: { videoId: string; startSeconds: number }): void;
  stopVideo(): void;
  mute(): void;
  unMute(): void;
  playVideo(): void;
  destroy(): void;
}

let apiReady: Promise<void> | null = null;
function loadYoutubeApi(): Promise<void> {
  apiReady ??= new Promise((resolve) => {
    const w = window as unknown as { YT?: { Player: unknown }; onYouTubeIframeAPIReady?: () => void };
    if (w.YT?.Player) return resolve();
    w.onYouTubeIframeAPIReady = () => resolve();
    const s = document.createElement('script');
    s.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(s);
  });
  return apiReady;
}

/**
 * Table DJ: one YouTube video for the whole table, started at the same offset for everyone (server `startedAt`).
 * Nothing playing: two tabs on the right edge order a music video or a song. Playing: the DJ on a vinyl, the mute
 * toggle, "F" to enlarge the video, and the video itself. The player stays muted until music is turned on.
 */
export function MusicDock({ room }: { room: PublicRoomState }) {
  const t = useT();
  const me = useSession((s) => s.me)!;
  const config = useSession((s) => s.config);
  const openSheet = useUi((s) => s.openSheet);
  const current = room.music.current;
  const host = useRef<HTMLDivElement>(null);
  const player = useRef<YtPlayer | null>(null);
  const loaded = useRef<string | null>(null);
  const [state, setState] = useState<PlayerState>('idle');
  // The video's size, chosen per track: a music video starts large, a song small; "F" or a tap switches it.
  const [size, setSize] = useState<{ track: string; large: boolean } | null>(null);

  useEffect(() => {
    if (!current || !host.current) {
      player.current?.destroy();
      player.current = null;
      loaded.current = null;
      return;
    }
    let cancelled = false;
    void loadYoutubeApi().then(() => {
      if (cancelled || !host.current) return;
      const startSeconds = Math.max(0, (serverNow() - current.startedAt) / 1000);
      if (!player.current) {
        const YT = (window as unknown as { YT: { Player: new (el: HTMLElement, o: object) => YtPlayer } }).YT;
        // The API replaces its target with an iframe, so give it an element React does not manage.
        const mount = document.createElement('div');
        host.current.replaceChildren(mount);
        player.current = new YT.Player(mount, {
          videoId: current.videoId,
          playerVars: {
            autoplay: 1,
            controls: 0,
            disablekb: 1,
            playsinline: 1,
            modestbranding: 1,
            rel: 0,
            start: Math.floor(startSeconds),
          },
          host: 'https://www.youtube-nocookie.com',
          events: {
            onReady: (e: { target: YtPlayer }) => {
              if (me.musicEnabled) e.target.unMute();
              else e.target.mute();
              e.target.playVideo();
            },
            onStateChange: (e: { data: number }) => setState(YT_STATES[e.data] ?? 'loading'),
            onError: () => setState('error'),
          },
        });
      } else if (loaded.current !== current.trackId) {
        player.current.loadVideoById({ videoId: current.videoId, startSeconds });
      }
      loaded.current = current.trackId;
    });
    return () => {
      cancelled = true;
    };
  }, [current, me.musicEnabled]);

  useEffect(() => {
    if (!player.current) return;
    if (me.musicEnabled) {
      player.current.unMute();
      player.current.playVideo();
    } else player.current.mute();
  }, [me.musicEnabled]);

  useEffect(() => () => player.current?.destroy(), []);

  const toggle = async () => {
    try {
      const updated = await http.patch<MeDto>('/me', { musicEnabled: !me.musicEnabled });
      useSession.getState().setMe(updated);
    } catch (err) {
      reportError(err);
    }
  };

  // Telegram's WebViews may block autoplay with sound until the player is started from a tap.
  const listen = () => {
    player.current?.unMute();
    player.current?.playVideo();
    if (!me.musicEnabled) void toggle();
  };

  const shown: PlayerState = current ? state : 'idle';
  const large = current ? (size?.track === current.trackId ? size.large : Boolean(current.video)) : false;
  const resize = () => current && setSize({ track: current.trackId, large: !large });
  const dj = current ? room.seats.find((s) => s?.userId === current.byUserId) : null;
  return (
    <div className="music-dock" data-testid="music" data-player-state={shown}>
      {current ? (
        <div className="music-card">
          <div className="music-dj">
            <button
              className={shown === 'playing' ? 'vinyl playing' : 'vinyl'}
              onClick={() => openSheet('dj')}
              title={`${t('table.now_playing')}: ${current.title} · ${current.byName}`}
              aria-label={t('table.dj_title')}
            >
              <Avatar name={current.byName} photoUrl={dj?.photoUrl ?? null} size="100%" />
            </button>
            <div className="music-controls">
              <button
                className="music-mute"
                onClick={() => void toggle()}
                aria-label={me.musicEnabled ? t('table.music_on') : t('table.music_off')}
                data-testid="music-toggle"
              >
                <SpeakerIcon muted={!me.musicEnabled} />
              </button>
              <button
                className={large ? 'music-f on' : 'music-f'}
                onClick={resize}
                aria-label={t('table.video_size')}
                aria-pressed={large}
                data-testid="music-size"
              >
                F
              </button>
            </div>
          </div>
          <div
            className={large ? 'music-video large' : 'music-video'}
            onClick={resize}
            data-testid="music-video"
          >
            <div ref={host} />
            {shown !== 'playing' && (
              <button
                className="music-listen"
                onClick={(e) => {
                  e.stopPropagation();
                  listen();
                }}
                data-testid="listen"
              >
                {shown === 'error' ? t('table.music_error') : `▶ ${t('table.tap_to_listen')}`}
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          <button
            className="music-tab"
            onClick={() => openSheet('dj', { video: true })}
            title={t('table.order_video', { price: config?.economy.djVideoPrice ?? 9 })}
            aria-label={t('table.order_video', { price: config?.economy.djVideoPrice ?? 9 })}
            data-testid="order-video"
          >
            <MusicVideoIcon />
          </button>
          <button
            className="music-tab"
            onClick={() => openSheet('dj')}
            title={t('table.order_song', { price: config?.economy.djPrice ?? 5 })}
            aria-label={t('table.order_song', { price: config?.economy.djPrice ?? 5 })}
            data-testid="order-song"
          >
            <SongIcon />
          </button>
        </>
      )}
    </div>
  );
}
